import { useCurrentUser } from '@artic/api-client';

/**
 * Resolves the cookie's `partner_key` to the partner's UUID `partner_id`.
 * Uses the same resolution logic as `usePartnerName`.
 */
export function usePartnerId(): string | undefined {
  const { partners, activePartner } = useCurrentUser();

  if (partners.length === 0) return undefined;

  const matched = partners.find((p) => p.partner_key === activePartner);
  if (matched) return matched.partner_id;

  const byName = partners.find((p) => p.partner_name === activePartner);
  if (byName) return byName.partner_id;

  return partners[0]?.partner_id;
}

/**
 * Resolves the cookie's `partner_key` (e.g. "LOW") to the full `partner_name`
 * (e.g. "Lowfield Timber Frames") that the API expects.
 *
 * Matches the `activePartner` value from the auth cookie against the
 * `partner_key` field in the user's partners list from `/current-user`.
 *
 * Falls back to the first partner if no match is found (handles the case
 * where the cookie contains a stale or unrecognised value).
 */
export function usePartnerName(): string | undefined {
  const { partners, activePartner } = useCurrentUser();

  if (partners.length === 0) return undefined;

  // Match cookie partner_key against the partner_key field
  const matched = partners.find((p) => p.partner_key === activePartner);
  if (matched) return matched.partner_name;

  // Fallback: if the cookie already contains a valid partner_name, use it directly
  const byName = partners.find((p) => p.partner_name === activePartner);
  if (byName) return byName.partner_name;

  // Last resort: use first partner
  return partners[0]?.partner_name;
}
